import { uniq } from 'lodash';

import { GAMIFICATION_EVENT_TYPES } from '@didaskill/domain/gamification-events/constants';
import {
  ACHIEVEMENT_MODES,
  ACHIEVEMENT_TIERS,
  ACHIEVEMENT_TYPES,
} from '@didaskill/domain/user-achievements/constants';
import { wasLastSessionOnPreviousWeekend } from '@didaskill/domain/users/shared';
import dayjs from '@didaskill/services/dayjs';

import type { GamificationRule } from '../types';

export const GAMIFICATION_RULES: Record<ACHIEVEMENT_TYPES, GamificationRule> = {
  [ACHIEVEMENT_TYPES.CAMPUS_EXPLORER]: {
    type: ACHIEVEMENT_TYPES.CAMPUS_EXPLORER,
    mode: ACHIEVEMENT_MODES.QUEST,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.QUEST,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.APPOINTMENT_SCHEDULED,
            threshold: 1,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.VIRTUAL_CLASSROOM_ATTENDED,
            threshold: 1,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.LESSON_COMPLETED,
            threshold: 1,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.PROFILE_PICTURE_UPDATED,
            threshold: 1,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.AUDIO_LISTENED,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.CAMPUS_EXPLORER,
          },
        ],
      },
    ],
    availability: {
      relativeToUser: {
        offsetDays: 0,
      },
      absolute: {
        start: new Date('2025-09-11'),
      },
    },
  },
  [ACHIEVEMENT_TYPES.GOOD_HABITS]: {
    type: ACHIEVEMENT_TYPES.GOOD_HABITS,
    mode: ACHIEVEMENT_MODES.QUEST,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.QUEST,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.FLASHCARDS_DECK_CREATED,
            threshold: 1,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.ACTIVE_SESSION,
            threshold: 15,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.EXERCISE_COMPLETED,
            threshold: 2,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.LEARNING_RESOURCE_VIEW,
            threshold: 1,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.LESSON_ASK_IA,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.GOOD_HABITS,
          },
        ],
      },
    ],
    availability: {
      relativeToUser: {
        offsetDays: 30,
      },
      absolute: {
        start: new Date('2025-09-11'),
      },
    },
  },
  [ACHIEVEMENT_TYPES.GAIN_CONFIDENCE]: {
    type: ACHIEVEMENT_TYPES.GAIN_CONFIDENCE,
    mode: ACHIEVEMENT_MODES.QUEST,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.QUEST,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.ACTIVE_SESSION,
            threshold: 20,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.FEEDBACK_CAMPAIGN_ANSWERED,
            threshold: 1,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.FLASHCARDS_DECK_COMPLETED,
            threshold: 1,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.MULTIPLE_EXERCISE_ATTEMPTS_COMPLETED,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.GAIN_CONFIDENCE,
          },
        ],
      },
    ],
    availability: {
      relativeToUser: {
        offsetDays: 60,
      },
      absolute: {
        start: new Date('2025-09-11'),
      },
    },
  },
  [ACHIEVEMENT_TYPES.SNAP_FINGERS]: {
    type: ACHIEVEMENT_TYPES.SNAP_FINGERS,
    mode: ACHIEVEMENT_MODES.QUEST,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.QUEST,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.APPOINTMENT_SCHEDULED,
            threshold: 1,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.EXERCISE_COMPLETED,
            threshold: 3,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.LESSON_COMPLETED,
            threshold: 2,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.VIRTUAL_CLASSROOM_ATTENDED,
            threshold: 2,
          },
          {
            type: GAMIFICATION_EVENT_TYPES.EXERCISE_PERFECT_SCORE,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.SNAP_FINGERS,
          },
        ],
      },
    ],
    availability: {
      relativeToUser: {
        offsetDays: 90,
      },
      absolute: {
        start: new Date('2025-09-11'),
      },
    },
  },
  [ACHIEVEMENT_TYPES.PROFILE_PICTURE_ADDED]: {
    type: ACHIEVEMENT_TYPES.PROFILE_PICTURE_ADDED,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.NEUTRAL,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.PROFILE_PICTURE_UPDATED,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.PROFILE_PICTURE_ADDED,
          },
        ],
      },
    ],
  },
  [ACHIEVEMENT_TYPES.CONNECTION_STREAK]: {
    type: ACHIEVEMENT_TYPES.CONNECTION_STREAK,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.NEUTRAL,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.ACTIVE_SESSION,
            threshold: 5,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.CONNECTION_STREAK,
          },
        ],
      },
      {
        tier: ACHIEVEMENT_TIERS.BRONZE,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.ACTIVE_SESSION,
            threshold: 10,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.CONNECTION_STREAK,
          },
        ],
      },
      {
        tier: ACHIEVEMENT_TIERS.SILVER,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.ACTIVE_SESSION,
            threshold: 30,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.CONNECTION_STREAK,
          },
        ],
      },
      {
        tier: ACHIEVEMENT_TIERS.GOLD,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.ACTIVE_SESSION,
            threshold: 100,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.CONNECTION_STREAK,
          },
        ],
      },
    ],
    shouldAggregate: (occurredAt, lastOccurredAt) => {
      const isLastSessionOnPreviousWeekend = wasLastSessionOnPreviousWeekend(
        lastOccurredAt as string,
      );

      return (
        !lastOccurredAt ||
        dayjs(occurredAt).startOf('day').diff(dayjs(lastOccurredAt).startOf('day'), 'day') === 1 ||
        isLastSessionOnPreviousWeekend
      );
    },
    shouldReset: (occurredAt, lastOccurredAt) => {
      const isLastSessionOnPreviousWeekend = wasLastSessionOnPreviousWeekend(
        lastOccurredAt as string,
      );

      return !!(
        lastOccurredAt &&
        dayjs(occurredAt).startOf('day').diff(dayjs(lastOccurredAt).startOf('day'), 'day') > 1 &&
        !isLastSessionOnPreviousWeekend
      );
    },
  },
  [ACHIEVEMENT_TYPES.EASTER_EGGS_2025]: {
    type: ACHIEVEMENT_TYPES.EASTER_EGGS_2025,
    mode: ACHIEVEMENT_MODES.BADGE,
    /** It is not planned to move events from tracking to gamification. ¯\_(ツ)_/¯ */
    levels: [],
    availability: {
      absolute: {
        start: new Date('2025-04-01'),
        end: new Date('2025-04-30'),
      },
    },
  },
  [ACHIEVEMENT_TYPES.EXERCISE_COMPLETED]: {
    type: ACHIEVEMENT_TYPES.EXERCISE_COMPLETED,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.NEUTRAL,
        events: [{ type: GAMIFICATION_EVENT_TYPES.EXERCISE_COMPLETED, threshold: 1 }],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.EXERCISE_COMPLETED,
          },
        ],
      },
    ],
  },
  [ACHIEVEMENT_TYPES.EXERCISE_PERFECT_SCORE]: {
    type: ACHIEVEMENT_TYPES.EXERCISE_PERFECT_SCORE,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.GOLD,
        events: [{ type: GAMIFICATION_EVENT_TYPES.EXERCISE_PERFECT_SCORE, threshold: 1 }],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.EXERCISE_PERFECT_SCORE,
          },
        ],
      },
    ],
  },
  [ACHIEVEMENT_TYPES.LESSON_AUDIO_LISTENED]: {
    type: ACHIEVEMENT_TYPES.LESSON_AUDIO_LISTENED,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.NEUTRAL,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.AUDIO_LISTENED,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.LESSON_AUDIO_LISTENED,
          },
        ],
      },
    ],
  },
  [ACHIEVEMENT_TYPES.FEEDBACK_CAMPAIGN_ANSWERED]: {
    type: ACHIEVEMENT_TYPES.FEEDBACK_CAMPAIGN_ANSWERED,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.NEUTRAL,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.FEEDBACK_CAMPAIGN_ANSWERED,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.FEEDBACK_CAMPAIGN_ANSWERED,
          },
        ],
      },
    ],
  },
  [ACHIEVEMENT_TYPES.HELP_IT_TEAM]: {
    type: ACHIEVEMENT_TYPES.HELP_IT_TEAM,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.NEUTRAL,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.HELP_IT_TEAM,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.HELP_IT_TEAM,
          },
        ],
      },
    ],
  },
  [ACHIEVEMENT_TYPES.LESSON_COMPLETED]: {
    type: ACHIEVEMENT_TYPES.LESSON_COMPLETED,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.NEUTRAL,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.LESSON_COMPLETED,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.LESSON_COMPLETED,
          },
        ],
      },
    ],
  },
  [ACHIEVEMENT_TYPES.APPOINTMENT_SCHEDULED]: {
    type: ACHIEVEMENT_TYPES.APPOINTMENT_SCHEDULED,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.NEUTRAL,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.APPOINTMENT_SCHEDULED,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.APPOINTMENT_SCHEDULED,
          },
        ],
      },
    ],
  },
  [ACHIEVEMENT_TYPES.VIRTUAL_CLASSROOM_ATTENDED]: {
    type: ACHIEVEMENT_TYPES.VIRTUAL_CLASSROOM_ATTENDED,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.NEUTRAL,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.VIRTUAL_CLASSROOM_ATTENDED,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.VIRTUAL_CLASSROOM_ATTENDED,
          },
        ],
      },
    ],
  },
  [ACHIEVEMENT_TYPES.CLASS_JOINED]: {
    type: ACHIEVEMENT_TYPES.CLASS_JOINED,
    mode: ACHIEVEMENT_MODES.BADGE,
    levels: [
      {
        tier: ACHIEVEMENT_TIERS.NEUTRAL,
        events: [
          {
            type: GAMIFICATION_EVENT_TYPES.CLASS_JOINED,
            threshold: 1,
          },
        ],
        awards: [
          {
            achievement: ACHIEVEMENT_TYPES.CLASS_JOINED,
          },
        ],
      },
    ],
  },
} as const;

export const achievementTypesByEventType = Object.values(GAMIFICATION_RULES).reduce(
  (acc, rule) => {
    rule.levels.forEach((level) => {
      level.events.forEach((event) => {
        acc[event.type] = acc[event.type] || [];
        acc[event.type].push(rule.type);
      });
    });
    return acc;
  },
  {} as Record<GAMIFICATION_EVENT_TYPES, ACHIEVEMENT_TYPES[]>,
);

export const eventTypesByAchievementType = Object.values(GAMIFICATION_RULES).reduce(
  (acc, rule) => {
    return {
      // biome-ignore lint/performance/noAccumulatingSpread: should be refactored
      ...acc,
      [rule.type]: uniq(rule.levels.flatMap((level) => level.events.map(({ type }) => type))),
    };
  },
  {} as Record<ACHIEVEMENT_TYPES, GAMIFICATION_EVENT_TYPES[]>,
);
